import { useState } from 'react';
import type { Event } from '@buzzmap/shared';
import { fallbackImage } from '../../utils/brandImages';
import { imageLoadingProps } from '../../utils/imagePriority';
import { useDeferredPhotoUrl } from '../../utils/deferredPhoto';

interface EventCoverImageProps {
  event: Event;
  className?: string;
  /**
   * Attributo `sizes` del solo ripiego locale (che ha varianti note): le foto esterne
   * non ne hanno e non ricevono srcset inventati.
   */
  sizes: string;
  /** Candidato LCP della vista iniziale: eager + fetchpriority high. Default: lazy. */
  priority?: boolean;
}

/**
 * Copertina di una carta evento, ordine: foto sponsor, foto raccolta (solo https), ripiego di
 * categoria (derivati WebP di T104, `brandImages`).
 *
 * Una sola immagine alla volta: il ripiego si scarica soltanto se la foto fallisce, mai insieme
 * ad essa. Se anche il derivato fallisce si torna all'originale una volta (`brandImages`).
 *
 * T204-INT: finché il guscio server tiene nascosta l'app, una foto di un altro host non parte
 * (nessun `src`, stessa scatola): scaricarla ora toglie banda a ciò che il rilascio aspetta.
 * Parte al rilascio; senza guscio parte subito, come prima.
 */
export function EventCoverImage({
  event,
  className,
  sizes,
  priority = false,
}: EventCoverImageProps) {
  const scraped = event.imageUrl?.startsWith('https://') ? event.imageUrl : undefined;
  const photo = event.sponsorImages?.[0] ?? scraped;
  // Si ricorda quale foto è fallita, non un booleano: cambiando foto si riprova.
  const [failedPhoto, setFailedPhoto] = useState<string | null>(null);
  const startablePhoto = useDeferredPhotoUrl(photo);

  const source =
    photo !== undefined && photo !== failedPhoto
      ? { src: startablePhoto, onError: () => setFailedPhoto(photo) }
      : fallbackImage(event.category, sizes);

  return (
    <img
      {...source}
      alt=""
      className={className}
      referrerPolicy="no-referrer"
      {...imageLoadingProps(priority)}
    />
  );
}
